import {ShiftEntity} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {IconMessagePlus} from "@tabler/icons-react";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntityCommentList} from "../../comment/EntityCommentList/EntityCommentList";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {ShiftTimeline} from "../../timeline/ShiftTimeline/ShiftTimeline";

export type ShiftDetailsProps = {
  shift: ShiftEntity
}

export const ShiftDetails = (props: ShiftDetailsProps) => {
  const {shift} = props;

  const {t} = useTranslation(["translation", "common"]);

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: shift
  });

  return (
    <Grid>
      <Grid.Col span={8}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.shift.profile.info.section.details.label", "Details")}</Title>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.shift.profile.info.field.person.label", "Driver")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={shift.personName}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.shift.profile.info.field.start.label", "Start Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={shift.startDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.shift.profile.info.field.end.label", "End Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={shift.endDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.shift.profile.info.field.vehicle.label", "Truck")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {shift.vehicleId ? <DefaultText value={shift.vehicleName}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.shift.profile.info.field.trailer.label", "Trailer")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {shift.trailerId ? <DefaultText value={shift.trailerName}/> : undefined}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>

        <Card shadow="sm" mt="lg" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.shift.profile.info.section.comments.label", "Comments")}</Title>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={handlers.open}>
                <IconMessagePlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <EntityCommentList objectId={shift.id}/>
            </Grid.Col>
          </Grid>
        </Card>

      </Grid.Col>

      <Grid.Col span={4}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.shift.info.section.timeline.label", "Timeline")}</Title>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <ShiftTimeline
                m="md"
                bulletSize={24}
                lineWidth={2}
                shift={shift}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>

    </Grid>
  );
};

